import { ReactNode, useState } from "react";
import { useMobile } from "@rapid/hooks/useMobile";
import SocialLoginButtons from "./SocialLoginButtons";
import EmailLinkForm from "./EmailLinkForm";
import { ModalWithIcon } from "@components/RfModal/ModalWithIcon";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsAuthenticationEvent } from "@rapid/analytics/types";
import { noop } from "lodash";
import { RfModal } from "@scripts/react/components/RfModal/RfModal";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { IconFrame } from "@scripts/react/components/IconFrame";
import { WallOfLove } from "./WallOfLove";

interface LoginComponentProps {
    isOpen?: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    redirectPath?: string;
    title?: string | ReactNode;
}

export default function LoginComponent({
    isOpen = true,
    onClose = noop,
    onSuccess,
    redirectPath = "/need-auth",
    title = "Sign In or Create An Account"
}: LoginComponentProps) {
    const { track } = useAnalytics();
    const isSmallScreen = useMobile(1024);
    const [hasSentLink, setHasSentLink] = useState(false);
    const [email, setEmail] = useState("");

    const handleSuccess = (u: any) => {
        if (onSuccess) {
            onSuccess();
        }

        track({
            event: AnalyticsAuthenticationEvent.USER_SIGNED_IN,
            data: { email: u.email, method: u.providerData[0]?.providerId, name: u.displayName }
        });
    };

    if (isSmallScreen) {
        return (
            <ModalWithIcon
                isOpen={isOpen}
                onClose={onClose}
                icon={getLoginIcon(hasSentLink)}
                title={getLoginTitle(hasSentLink, title)}
                size="sm"
                showFooter={false}
                className="gap-6"
                panelClassName="gap-6"
            >
                <LoginFormContent
                    handleSuccess={handleSuccess}
                    redirectPath={redirectPath}
                    hasSentLink={hasSentLink}
                    setHasSentLink={setHasSentLink}
                    email={email}
                    setEmail={setEmail}
                />
            </ModalWithIcon>
        );
    }

    return (
        <RfModal isOpen={isOpen} onClose={onClose}>
            <RfModal.Overlay />
            <RfModal.Panel className="relative flex h-[90vh] !max-w-[90vw] flex-row items-stretch rounded-3xl p-0">
                <RfModal.CloseButton />

                <div className="flex w-full items-center justify-center">
                    <LoginSection
                        handleSuccess={handleSuccess}
                        redirectPath={redirectPath}
                        title={title}
                        hasSentLink={hasSentLink}
                        setHasSentLink={setHasSentLink}
                        email={email}
                        setEmail={setEmail}
                    />
                </div>
                <WallOfLove />
            </RfModal.Panel>
        </RfModal>
    );
}

function getLoginIcon(hasSentLink: boolean): string {
    return hasSentLink ? "fad fa-envelope" : "fad fa-user";
}

function getLoginTitle(hasSentLink: boolean, defaultTitle: string | ReactNode): string | ReactNode {
    return hasSentLink ? "We emailed you a link to log in" : defaultTitle;
}

function LoginFormContent({
    handleSuccess,
    redirectPath,
    hasSentLink,
    setHasSentLink,
    email,
    setEmail
}: {
    handleSuccess: (user: any) => void;
    redirectPath: string;
    hasSentLink: boolean;
    setHasSentLink: (hasSentLink: boolean) => void;
    email: string;
    setEmail: (email: string) => void;
}) {
    return (
        <>
            <EmailLinkForm
                hasSentLink={hasSentLink}
                setHasSentLink={setHasSentLink}
                email={email}
                setEmail={setEmail}
            />
            {!hasSentLink && (
                <>
                    <div className="flex items-center gap-4">
                        <div className="flex-grow border-t border-divider"></div>
                        <span className="flex-shrink-0 text-xs text-tertiary">OR</span>
                        <div className="flex-grow border-t border-divider"></div>
                    </div>
                    <SocialLoginButtons onSuccess={handleSuccess} redirectPath={redirectPath} />
                </>
            )}
        </>
    );
}

function LoginSection({
    handleSuccess,
    redirectPath,
    title,
    hasSentLink,
    setHasSentLink,
    email,
    setEmail
}: {
    handleSuccess: (user: any) => void;
    redirectPath: string;
    title: string | ReactNode;
    hasSentLink: boolean;
    setHasSentLink: (hasSentLink: boolean) => void;
    email: string;
    setEmail: (email: string) => void;
}) {
    return (
        <div className="mx-16 flex w-full max-w-lg flex-col gap-6 rounded-xl">
            <div className={twClassNames("mt-2 flex flex-col gap-3")}>
                <div className="flex justify-center">
                    <IconFrame
                        icon={getLoginIcon(hasSentLink)}
                        size="lg"
                        isCircle={true}
                        color="purple"
                    />
                </div>
                <span
                    className={twClassNames(
                        "w-full text-center text-lg font-semibold text-gray-900"
                    )}
                >
                    {getLoginTitle(hasSentLink, title)}
                </span>
            </div>
            <div className="flex flex-col gap-4 text-base text-gray-700">
                <LoginFormContent
                    handleSuccess={handleSuccess}
                    redirectPath={redirectPath}
                    hasSentLink={hasSentLink}
                    setHasSentLink={setHasSentLink}
                    email={email}
                    setEmail={setEmail}
                />
            </div>
        </div>
    );
}
